<template>
  <VaCard class="h-full">
    <VaCardContent>
      <section>
        <header class="flex items-center justify-between">
          <div class="flex flex-col gap-2 sm:items-start">
            <VaButtonToggle v-model="selectedMuelle" color="#EFEFEF" toggle-color="primary" :options="muelles" />
            <h3 class="mb-3 font-bold text-lg">Embarcaciones con deuda por muelle</h3>
          </div>
          <div class="flex flex-col gap-2 sm:items-end">
            <div
              class="p-1 rounded"
              :style="{
                backgroundColor: iconBackground,
                color: iconColor,
              }"
            >
              <slot name="icon"></slot>
            </div>
            <div class="text-lg font-semibold grow">{{ debtNaves }} / {{ totalNaves }}</div>
          </div>
        </header>
        <div class="body-content">
          <!--
          <p class="text-xs text-secondary">
            <span :class="changeClass">
              <template v-if="up">↑</template>
              <template v-else>↓</template>
              {{ changeText }}
            </span>
            desde el mes pasado
          </p>
          -->
          <div class="scrollable-data-list">
            <VaDataTable
              class="region-revenue-table"
              :columns="[
                { key: 'Nave', label: 'Embarcación' },
                { key: 'debt', label: 'Deuda', align: 'left' },
                { key: 'state', label: 'Estado' },
              ]"
              :items="dataList"
            >
              <template #cell(Nave)="{ rowData }">
                {{ rowData.tipo_embarcacion == 'auxiliar' ? rowData.nave_madre : rowData.nombre_embarcacion }}
              </template>
              <template #cell(debt)="{ rowData }">
                {{
                  rowData.debt > 0
                    ? '$' + rowData.debt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    : 'No hay deuda'
                }}
              </template>
              <template #cell(state)="{ rowData }">
                <template v-if="rowData.requestData">
                  <template v-if="rowData.requestData!.estado == RequestStatus.APPROVED">
                    <template v-if="new Date(rowData.requestData!.fecha_caducidad) > new Date()">
                      <VaChip color="success" size="small" icon="check"> Habilitado </VaChip>
                    </template>
                    <template v-else>
                      <VaChip color="warning" size="small" icon="error"> Caducado </VaChip>
                    </template>
                  </template>
                  <template v-else-if="rowData.requestData!.estado == RequestStatus.REJECTED">
                    <VaChip color="danger" size="small" icon="close"> Deshabilitado </VaChip>
                  </template>
                  <template v-else-if="rowData.requestData!.estado == RequestStatus.PENDING">
                    <VaChip color="default" size="small" icon="pending"> Pendiente </VaChip>
                  </template>
                </template>
              </template>
            </VaDataTable>
          </div>
        </div>
      </section>
    </VaCardContent>
  </VaCard>
</template>

<script lang="ts" setup>
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { useColors, useModal, useToast } from 'vuestic-ui'
import { VaCard } from 'vuestic-ui'
import { NavesProperties } from '../../../../data/NavesProperties'

import roleCapacitiesManager, {
  UserRoles,
  ExternalSpec,
  ArmadorType,
  UserRole,
  getRoleName,
} from '../../../../managers/identity/RoleCapacitiesManager'
import { IEmbarcacion, OperatedEmbarcacion } from '../../../../services/api-services/EmbarcacionesServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import { IRequest, RequestObjectType, RequestStatus } from '../../../../services/api-services/RequestsServices'
import identityManager, { LoggedInUser } from '../../../../managers/identity/IdentityManager'
import { CobroEstado, CobroMuelle, ICobro, OperatedCobro } from '../../../../services/api-services/CobrosServices'
import { IViaje, OperatedViaje, ViajeCancelado, ViajeEstado } from '../../../../services/api-services/ViajesServices'
import { IService, OperatedService } from '../../../../services/api-services/ServicesServices'
import OperadorTarifas from '../../../../managers/motores/utilities/OperadorTarifas'
import MotorMatematico from '../../../../managers/motores/MotorMatematico'
import { ITarifa, UnidadMedidaAplicada } from '../../../../services/api-services/TarifasServices'
import {
  defaultIIncidencia,
  IIncidencia,
  IncidenceCode,
  IncidenceType,
} from '../../../../services/api-services/BitacoraServices'
import { IArmador } from '../../../../services/api-services/ArmadoresServices'
import { IPermisoControl } from '../../../../services/api-services/RepresentantesServices'
import { resolveEmbarcacionAccess } from '../../../../services/utils/PermisosResolver'

const activeArmadorIdRef = identityManager.getRefActiveArmadorId()
const armadoresList: Ref<IArmador[]> = dataStorageManager.subscribeBucket<IArmador>(
  BucketDefaultIDs.ARMADORES,
  () => {},
)
const armadorId = computed<number | null>(() => {
  if (activeArmadorIdRef.value !== null) return activeArmadorIdRef.value
  const user = identityManager.getUser()
  if (!user) return null
  const armador = armadoresList.value.find((a) => a.rut_representante.toString() === user.rut.toString())
  return armador?.id ?? null
})
const armadorData = computed<IArmador | null>(() =>
  armadorId.value !== null ? armadoresList.value.find((a) => a.id === armadorId.value) ?? null : null,
)

const { init: notify } = useToast()
const { confirm } = useModal()

const muelles = [
  {
    label: 'Muelle Este',
    value: 0,
  },
  {
    label: 'Muelle Weste',
    value: 1,
  },
]
const selectedMuelle: Ref<number> = ref(0)

interface debtEmbarcacion extends OperatedEmbarcacion {
  debt: number
}

const { getColor } = useColors()

const operadorTarifas = new OperadorTarifas()

const totalNaves: Ref<number> = ref(50)
const debtNaves: Ref<number> = ref(4)

const cobrosRequests: Ref<IRequest[]> = ref([])
const cobrosList: Ref<ICobro[]> = ref([])
const viajesList: Ref<IViaje[]> = ref([])
const fullViajesList: Ref<OperatedViaje[]> = ref([])
const posicionamientosList: Ref<IRequest[]> = ref([])
const permisosMuelleWesteList: Ref<IRequest[]> = ref([])
const servicesList: Ref<IService[]> = ref([])
const fullServicesList: Ref<OperatedService[]> = ref([])
const permisosServiciosList: Ref<IRequest[]> = ref([])
const tarifasEspecialesList: Ref<IRequest[]> = ref([])

const embarcacionesList: Ref<IEmbarcacion[]> = ref([])
const fullEmbarcacionesList: Ref<debtEmbarcacion[]> = ref([])
const showList: Ref<debtEmbarcacion[]> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])
// Solicitudes de representación (apoderado) del armador activo + matriz de permisos, para acotar
// la deuda del apoderado a SÓLO sus embarcaciones asignadas (RN acordada 03/06 CASO 3). El armador
// directo y el representante legal no se filtran (ven toda la flota).
const representanteSolsList: Ref<IRequest[]> = ref([])
const permisosList: Ref<IPermisoControl[]> = dataStorageManager.subscribeBucket<IPermisoControl>(
  BucketDefaultIDs.PERMISOS,
  () => {},
)

// Permisos del apoderado activo sobre el armador en contexto; null cuando no aplica filtro
// (armador directo o representante legal → acceso pleno a la flota).
const apoderadoPermisoInfo = computed<IPermisoControl[] | null>(() => {
  if (!roleCapacitiesManager.isActingAsApoderado()) return null
  const user = identityManager.getUser()
  const activeId = activeArmadorIdRef.value
  if (!user || activeId === null) return null
  const mySol = representanteSolsList.value.find(
    (r) =>
      r.tipo_objeto === RequestObjectType.RepresentanteArmador &&
      Number(r.id_objeto) === activeId &&
      Number(r.id_objeto_2) === user.id_usuario &&
      r.estado === RequestStatus.APPROVED,
  )
  if (!mySol) return null
  return permisosList.value.filter((p) => Number(p.representante) === Number(mySol.id))
})

function canAccessNaveDeuda(embarcacionId: number): boolean {
  const info = apoderadoPermisoInfo.value
  if (info === null) return true
  return resolveEmbarcacionAccess(info, embarcacionId)
}

const selectedItemsEmitted: Ref<debtEmbarcacion[]> = ref([])

const iconBackground = getColor('danger')
const iconColor = getColor('on-danger')

const dataList: Ref<debtEmbarcacion[]> = ref([])

const tarifas: Ref<ITarifa[]> = dataStorageManager.subscribeBucket<ITarifa>(BucketDefaultIDs.TARIFAS, () => {})

onMounted(() => {
  dataStorageManager.subscribeBucket<IRequest>(BucketDefaultIDs.SOLICITUDES, (requests) => {
    onRequestsLoad(requests)
  })
  dataStorageManager.subscribeBucket<IViaje>(BucketDefaultIDs.VIAJES, (viajes) => {
    viajesList.value = viajes
    loadFullViajesList()
  })
  dataStorageManager.subscribeBucket<IService>(BucketDefaultIDs.SERVICES, (services) => {
    servicesList.value = services
    loadFullServicesList()
  })
  dataStorageManager.subscribeBucket<ICobro>(BucketDefaultIDs.COBROS, (cobros) => {
    cobrosList.value = cobros
    loadFullEmbarcaciones()
  })
  dataStorageManager.subscribeBucket<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES, (embarcaciones) => {
    embarcacionesList.value = embarcaciones
    loadFullEmbarcaciones()
  })
})

watch([fullEmbarcacionesList, selectedMuelle, armadorId, apoderadoPermisoInfo], () => {
  const navesList = fullEmbarcacionesList.value
    .filter((e) =>
      e.asociationsData.some(
        (a) =>
          (a.estado === RequestStatus.APPROVED || a.estado === RequestStatus.PENDING) &&
          armadorId.value !== null &&
          a.id_objeto_2 &&
          a.id_objeto_2.toString() === armadorId.value.toString(),
      ),
    )
    // Apoderado: sólo la deuda de las embarcaciones que tiene asignadas (RN 03/06 CASO 3).
    .filter((e) => canAccessNaveDeuda(e.id))
    .filter((e) => {
      if (!e.requestData) return false
      if (selectedMuelle.value == 0) {
        return e.tipo_embarcacion == 'turistica' || e.tipo_embarcacion == 'fletera'
      } else {
        return e.tipo_embarcacion == 'auxiliar' || e.tipo_embarcacion == 'pilotaje'
      }
    })
    .sort((a, b) => {
      const debtDif = b.debt - a.debt
      if (debtDif !== 0) {
        return debtDif
      } else {
        const nombreA = a.tipo_embarcacion == 'auxiliar' ? a.nave_madre : a.nombre_embarcacion
        const nombreB = b.tipo_embarcacion == 'auxiliar' ? b.nave_madre : b.nombre_embarcacion
        return nombreA.toString().localeCompare(nombreB.toString())
      }
    })
  dataList.value = navesList
  totalNaves.value = navesList.length
  debtNaves.value = navesList.filter((n) => n.debt > 0).length
})

watch([dataList], () => {
  showList.value = dataList.value
})

async function loadFullEmbarcaciones() {
  fullEmbarcacionesList.value = embarcacionesList.value.map((item) => {
    let request: IRequest | undefined = requestsList.value
      .filter((req) => req.tipo_objeto === RequestObjectType.Embarcacion)
      .filter((req) => req.id_objeto.toString() === item.id.toString())
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
    const asociationsData = asociationsRequests.value
      .filter((req) => req.id_objeto.toString() === item.id.toString())
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())

    const viajesData = fullViajesList.value.filter((viaje) => viaje.embarcacion_id.toString() === item.id.toString())
    const servicesData = fullServicesList.value.filter(
      (service) =>
        service.tipo_servicio === 'armador' && service.id_embarcacion_prestadora!.toString() === item.id.toString(),
    )

    const unidadMedidaAplicada: UnidadMedidaAplicada = {
      dia: 0,
      mmz: 0,
      zpe: 0,
      mes: 0,
    }

    const cobrosData: Array<OperatedCobro> = cobrosList.value
      .filter((cobro) => cobro.id_embarcacion === item.id)
      .map((cobro) => {
        const viajesApplied: IViaje[] = []
        const servicesApplied: IService[] = []
        const reqCobros = cobrosRequests.value
          .filter((req) => req.id_objeto.toString() === cobro.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())
        reqCobros.forEach((reqCobro) => {
          if (reqCobro.tipo_objeto_2 === 'viaje') {
            const viajeApplied = viajesData.find(
              (viaje) => reqCobro.id_objeto_2 !== null && viaje.id.toString() === reqCobro.id_objeto_2.toString(),
            )
            if (viajeApplied) {
              viajesApplied.push(viajeApplied)
            }
          } else {
            const serviceApplied = servicesData.find(
              (service) => service.id.toString() === reqCobro.id_objeto_2!.toString(),
            )
            if (serviceApplied) {
              servicesApplied.push(serviceApplied)
            }
          }
        })
        if (reqCobros.some((c) => c.estado === RequestStatus.APPROVED)) {
          cobro.estado = CobroEstado.PAGADO
        } else if (reqCobros.some((c) => c.estado === RequestStatus.REJECTED)) {
          cobro.estado = CobroEstado.RECHAZADO
        } else {
          cobro.estado = CobroEstado.PENDIENTE
        }

        return {
          ...cobro,
          cobrosRequests: reqCobros,
          viajesApplied: viajesApplied,
          servicesApplied: servicesApplied,
        }
      })

    const unusedViajes = viajesData
      .filter((v) => !ViajeCancelado(v) && v.estado !== ViajeEstado.TIMEOUT)
      .filter((viaje) => {
        return !cobrosData.some((cobro) =>
          cobro.viajesApplied.some((viajeApplied) => viajeApplied.id.toString() === viaje.id.toString()),
        )
      })
    const unusedServices = servicesData.filter((service) => {
      return !cobrosData.some((cobro) =>
        cobro.servicesApplied.some((serviceApplied) => serviceApplied.id.toString() === service.id.toString()),
      )
    })

    const currentCobros: Array<OperatedCobro> = []
    while (unusedViajes.length > 0 && unusedServices.length > 0) {
      const viajeEjemplo = unusedViajes[0]
      const serviceEjemplo = unusedServices[0]
      if (viajeEjemplo || serviceEjemplo) {
        const dateViaje = new Date(viajeEjemplo?.fecha_viaje || serviceEjemplo?.fecha_inicio)
        const monthStart = new Date(dateViaje.getFullYear(), dateViaje.getMonth(), 1)
        const monthEnd = new Date(dateViaje.getFullYear(), dateViaje.getMonth() + 1, 0, 23, 59, 59)
        const unusedViajesEsteInMonth = unusedViajes.filter((v) => {
          const dateV = new Date(v.fecha_viaje)
          return v.muelle === 'Muelle Este' && dateV >= monthStart && dateV <= monthEnd
        })
        const unusedViajesWesteInMonth = unusedViajes.filter((v) => {
          const dateV = new Date(v.fecha_viaje)
          return v.muelle === 'Muelle Weste' && dateV >= monthStart && dateV <= monthEnd
        })
        const unusedServicesInMonth = unusedServices.filter((s) => {
          const dateS = new Date(s.fecha_inicio)
          return dateS >= monthStart && dateS <= monthEnd
        })

        const cobrosViajesMuelleEste = operadorTarifas.getCobrosViajes(unusedViajesEsteInMonth)
        currentCobros.push(...cobrosViajesMuelleEste)
        const cobrosViajesMuelleWeste = operadorTarifas.getCobrosViajes(unusedViajesWesteInMonth)
        currentCobros.push(...cobrosViajesMuelleWeste)
        const cobrosServicios = operadorTarifas.getCobrosServicios(unusedServicesInMonth)
        currentCobros.push(...cobrosServicios)
        const unidadMedida = operadorTarifas.getUnitsDataNoDate(
          [...unusedViajesEsteInMonth, ...unusedViajesWesteInMonth],
          unusedServicesInMonth,
        )
        unidadMedidaAplicada.dia += unidadMedida.dia
        unidadMedidaAplicada.mmz += unidadMedida.mmz
        unidadMedidaAplicada.zpe += unidadMedida.zpe

        // Remove them from the main unused lists
        unusedViajesEsteInMonth.forEach((v) => {
          const index = unusedViajes.findIndex((uv) => uv.id === v.id)
          if (index !== -1) {
            unusedViajes.splice(index, 1)
          }
        })
        unusedViajesWesteInMonth.forEach((v) => {
          const index = unusedViajes.findIndex((uv) => uv.id === v.id)
          if (index !== -1) {
            unusedViajes.splice(index, 1)
          }
        })
        unusedServicesInMonth.forEach((s) => {
          const index = unusedServices.findIndex((us) => us.id === s.id)
          if (index !== -1) {
            unusedServices.splice(index, 1)
          }
        })
      }
    }

    const prevDebt = getMonthDebt({
      ...item,
      requestData: request,
      changesData: [],
      asociationData: asociationsData[0],
      asociationsData: asociationsData,
      cobrosData: cobrosData,
      currentCobrosData: currentCobros,
      salidasData: viajesData,
      serviciosData: servicesData,
      unidadesData: viajesData.length > 0 || servicesData.length > 0 ? unidadMedidaAplicada : undefined,
    })

    const currentDebt = getEventsDebt({
      ...item,
      requestData: request,
      changesData: [],
      asociationData: asociationsData[0],
      asociationsData: asociationsData,
      cobrosData: cobrosData,
      currentCobrosData: currentCobros,
      salidasData: viajesData,
      serviciosData: servicesData,
      unidadesData: viajesData.length > 0 || servicesData.length > 0 ? unidadMedidaAplicada : undefined,
    })

    const totalDebt =
      currentDebt.este.reduce((acc, curr) => acc + (curr.cobro.estado !== CobroEstado.PAGADO ? curr.total : 0), 0) +
      currentDebt.weste.reduce((acc, curr) => acc + (curr.cobro.estado !== CobroEstado.PAGADO ? curr.total : 0), 0) +
      prevDebt.reduce(
        (acc, curr) =>
          acc +
          curr.debt.este.reduce((a, c) => a + (c.cobro.estado !== CobroEstado.PAGADO ? c.total : 0), 0) +
          curr.debt.weste.reduce((a, c) => a + (c.cobro.estado !== CobroEstado.PAGADO ? c.total : 0), 0),
        0,
      )

    const matriculaValid = item.id_matricula !== 0
    const fotografiaValid = item.id_fotografía !== 0
    const certificadoValid = item.id_certifricado_navegacion !== 0

    const validDocs = fotografiaValid && matriculaValid && certificadoValid
    if (item.tipo_embarcacion != 'auxiliar' && !validDocs) {
      request = undefined
    }
    return {
      ...item,
      requestData: request,
      changesData: [],
      asociationData: asociationsData[0],
      asociationsData: asociationsData,
      cobrosData: cobrosData,
      currentCobrosData: currentCobros,
      salidasData: viajesData,
      serviciosData: servicesData,
      unidadesData: viajesData.length > 0 || servicesData.length > 0 ? unidadMedidaAplicada : undefined,
      debt: totalDebt,
    }
  })
}

function loadFullViajesList() {
  if (viajesList.value.length > 0 && posicionamientosList.value.length > 0) {
    fullViajesList.value = viajesList.value.map((viaje) => {
      const posicionamientos = posicionamientosList.value
        .filter((req) => req.id_objeto.toString() === viaje.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())
      let permisoMuelleWeste = permisosMuelleWesteList.value
        .filter((req) => req.id_objeto.toString() === viaje.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      if (viaje.muelle == 'Muelle Weste') {
        permisoMuelleWeste = permisosMuelleWesteList.value
          .filter((req) => req.id_objeto.toString() === viaje.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      }

      return {
        ...viaje,
        requestData: undefined,
        posicionamientosData: posicionamientos,
        asociationData: [],
        embarcacionData: undefined,
        armadorData: undefined,
        patronData: undefined,
        tripulantesData: [],
        permisoMuelleWesteData: permisoMuelleWeste,
      }
    })
    loadFullEmbarcaciones()
  }
}

function loadFullServicesList() {
  if (servicesList.value.length > 0 && tarifasEspecialesList.value.length > 0) {
    fullServicesList.value = servicesList.value.map((service) => {
      const requestData = permisosServiciosList.value
        .filter((req) => req.id_objeto.toString() === service.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      const tarifasData = tarifasEspecialesList.value
        .filter((req) => req.id_objeto.toString() === service.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())

      return {
        ...service,
        requestData: requestData,
        posicionamientosData: [],
        tarifasEspecialesData: tarifasData,
        vehiculosData: [],
        vehiculosRequestsData: [],
        usuarioSolicitanteData: undefined,
        embarcacionData: undefined,
      }
    })
    loadFullEmbarcaciones()
  }
}

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Embarcacion)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Asociacion_embarcacion)
  posicionamientosList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoViaje)
  permisosMuelleWesteList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PermisoMuelleWeste)
  permisosServiciosList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Servicio)
  tarifasEspecialesList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.TarifaEspecial)
  cobrosRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Cobro)
  representanteSolsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.RepresentanteArmador)

  loadFullViajesList()
  loadFullServicesList()
}

/*
const changeClass = computed(() => ({
  'text-success': props.up,
  'text-red-600': !props.up,
}))*/

const getTotalCurrentDebt = (): number => {
  const calulator = new MotorMatematico(tarifas.value)
  let total = 0
  fullEmbarcacionesList.value.forEach((embarcacion) => {
    const variables = {
      eslora: embarcacion.eslora_metros,
    }

    total += embarcacion.currentCobrosData.reduce((acc, cobro) => acc + cobro.valor_total, 0)
  })
  return total
}

const getTotalDebt = (): number => {
  const calulator = new MotorMatematico(tarifas.value)
  let total = 0
  fullEmbarcacionesList.value.forEach((embarcacion) => {
    const variables = {
      eslora: embarcacion.eslora_metros,
    }

    total += embarcacion.cobrosData.reduce((acc, cobro) => acc + cobro.valor_total, 0)
  })
  return total
}

function getEventsDebt(embarcacion: OperatedEmbarcacion): {
  este: Array<{ cobro: OperatedCobro; total: number }>
  weste: Array<{ cobro: OperatedCobro; total: number }>
} {
  const motorCalculos = new MotorMatematico(tarifas.value)

  const deudasEste = embarcacion.currentCobrosData
    .filter((c) => c.muelle === CobroMuelle.ESTE)
    .map((c) => ({
      cobro: c,
      total: c.valor_total,
    }))

  const deudasWeste = embarcacion.currentCobrosData
    .filter((c) => c.muelle === CobroMuelle.WESTE)
    .map((c) => ({
      cobro: c,
      total: c.valor_total,
    }))

  return {
    este: deudasEste,
    weste: deudasWeste,
  }
}

function getMonthDebt(embarcacion: OperatedEmbarcacion): Array<{
  month: string
  year: number
  paid: CobroEstado
  date: Date
  debt: {
    este: Array<{ cobro: OperatedCobro; total: number }>
    weste: Array<{ cobro: OperatedCobro; total: number }>
  }
}> {
  const motorCalculos = new MotorMatematico(tarifas.value)

  const monthDebtMap: Map<
    string,
    {
      month: string
      year: number
      paid: CobroEstado
      date: Date
      debt: {
        este: Array<{ cobro: OperatedCobro; total: number }>
        weste: Array<{ cobro: OperatedCobro; total: number }>
      }
    }
  > = new Map()

  embarcacion.cobrosData.forEach((cobro) => {
    const date = new Date(cobro.fecha.split('+')[0])
    const monthKey = `${date.getMonth()}-${date.getFullYear()}`

    if (!monthDebtMap.has(monthKey)) {
      monthDebtMap.set(monthKey, {
        month: date.toLocaleString('default', { month: 'long' }),
        year: date.getFullYear(),
        date: date,
        paid:
          cobro.cobrosRequests.length > 0
            ? cobro.cobrosRequests[0].estado === RequestStatus.APPROVED
              ? CobroEstado.PAGADO
              : CobroEstado.RECHAZADO
            : CobroEstado.PENDIENTE,
        debt: {
          este: [],
          weste: [],
        },
      })
    }

    const monthDebt = monthDebtMap.get(monthKey)!
    if (cobro.muelle === CobroMuelle.ESTE) {
      monthDebt.debt.este.push({
        cobro: cobro,
        total: cobro.valor_total,
      })
    } else if (cobro.muelle === CobroMuelle.WESTE) {
      monthDebt.debt.weste.push({
        cobro: cobro,
        total: cobro.valor_total,
      })
    }
  })

  return Array.from(monthDebtMap.values())
}

async function disableEmbarcacionesSeleccionadas() {
  const confirmed = await confirm({
    title: 'Confirmar deshabilitación',
    message: `¿Estás seguro de que deseas deshabilitar las embarcaciones seleccionadas por deuda pendiente?`,
    okText: 'Sí, deshabilitar',
    cancelText: 'Cancelar',
  })
  if (!confirmed) {
    return
  }
  try {
    const currentUser: LoggedInUser | null = identityManager.getUser()
    if (!currentUser) {
      throw new Error('Usuario no autenticado')
    }
    const embaracacionesPromises = selectedItemsEmitted.value.map(async (embarcacion) => {
      const requestData: IRequest = embarcacion.requestData!
      requestData.estado = RequestStatus.REJECTED
      requestData.revisor = currentUser.id_usuario
      requestData.razon_rechazo = 'Deshabilitado por deuda pendiente'
      await dataStorageManager.performOperation(BucketDefaultIDs.SOLICITUDES, 'updateRequest', requestData)

      const bitacoraData: IIncidencia = {
        ...defaultIIncidencia,
      }
      bitacoraData.tipo_incidencia_id = IncidenceType.EMBARCACION
      bitacoraData.codigo_incidencia = IncidenceCode.RECHAZO
      bitacoraData.descripcion = `Se desactiva embarcación ID ${embarcacion.id} por deuda caducada`
      bitacoraData.id_objeto = embarcacion.id
      await dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)
    })
    await Promise.all(embaracacionesPromises)
    notify({
      message: `Embarcaciones desactivadas correctamente`,
      color: 'success',
    })
  } catch (error) {
    console.error('Error disabling embarcaciones:', error)
    notify({
      message: `Error al rechazar la deuda: ${(error as Error).message}`,
      color: 'danger',
    })
  }
}
</script>

<style scoped>
.body-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.scrollable-data-list {
  max-height: 520px;
  overflow-y: auto;
}
</style>
